import React, { useRef, useEffect } from 'react';

export type SEOOpenGraphType = 'website' | 'article' | 'event';

export type StructuredData =
  | Record<string, unknown>
  | Array<Record<string, unknown>>;

export interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: SEOOpenGraphType;
  noindex?: boolean;
  structuredData?: StructuredData;
  lang?: string;
}

function toAbsoluteUrl(input: string | undefined, origin: string): string | undefined {
  if (!input) return undefined;
  if (input.startsWith('http://') || input.startsWith('https://')) return input;
  if (input.startsWith('//')) return `https:${input}`;
  if (input.startsWith('/')) return `${origin}${input}`;
  return `${origin}/${input}`;
}

const DEFAULT_TITLE = 'ToGather';
const DEFAULT_OG_IMAGE_PATH = '/opengraph-image.png';

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image,
  url,
  type = 'website',
  noindex = false,
  structuredData,
  lang = 'en',
}) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const fullTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  const scriptRef = useRef<HTMLScriptElement>(null);

  const canonicalUrl = url ? toAbsoluteUrl(url, origin) : undefined;
  const ogImage = toAbsoluteUrl(image || DEFAULT_OG_IMAGE_PATH, origin);

  // Set JSON-LD content via textContent (safer than dangerouslySetInnerHTML)
  useEffect(() => {
    if (scriptRef.current && structuredData) {
      scriptRef.current.textContent = JSON.stringify(structuredData);
    }
  }, [structuredData]);

  // React 19 hoists <title>/<meta>/<link> into <head>
  // so this can live anywhere in the component tree.
  return (
    <>
      <title>{fullTitle}</title>

      {description ? <meta name="description" content={description} /> : null}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description ? <meta property="og:description" content={description} /> : null}
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:type" content={type} />
      {lang ? <meta property="og:locale" content={lang} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {/* Canonical */}
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

      {/* Indexing */}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      {/* Structured data */}
      {structuredData ? (
        <script ref={scriptRef} type="application/ld+json" />
      ) : null}
    </>
  );
};

export default SEO;

